import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Popconfirm, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useTreatmentServiceDeclineTreatmentPlanRecipeEndpoint,
  useTreatmentServiceDispenseTreatmentPlanRecipeEndpoint,
  useTreatmentServiceGetTreatmentPlanRecipeEndpointKey,
  useTreatmentServiceGetTreatmentPlanRecipeListEndpoint,
  useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetTreatmentPlanRecipeListResponseItem,
  RecipeStatus
} from 'src/api/requests'
import { message, PageBreadcrumbs, SearchBar } from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

import { PrescriptionsStatusTag } from '../components'

import { useStyles } from './styles'

export const PrescriptionsListPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const navigate = useNavigate()

  const pagination = useTablePagination()
  const { userData } = useUser()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    legalEntityId: userData?.currentLegalEntityId,
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useTreatmentServiceGetTreatmentPlanRecipeListEndpoint(queryParams)

  const { mutate: decline, isPending: isDeclining } =
    useTreatmentServiceDeclineTreatmentPlanRecipeEndpoint({
      onSuccess: () => {
        message.success(t('recipes.success_declined'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeEndpointKey]
        })
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const { mutate: dispense, isPending: isDispensing } =
    useTreatmentServiceDispenseTreatmentPlanRecipeEndpoint({
      onSuccess: () => {
        message.success(t('recipes.success_dispensed'))
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetTreatmentPlanRecipeEndpointKey]
        })
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onSearch = (value: string) => {
    setSearch(value)
    resetPagination(pagination)
  }

  const onDispense = (id: string, treatmentPlanId: string) => {
    dispense({ treatmentPlanId, recipeId: id })
  }

  const onDecline = (id: string, treatmentPlanId: string) => {
    decline({ treatmentPlanId, recipeId: id })
  }

  const columns: ColumnsType<GetTreatmentPlanRecipeListResponseItem> = [
    {
      key: 'created date',
      title: t('investigations.created_date'),
      render: (_, { dateIssued }) =>
        dayjs(dateIssued).format(dayMonthYearHourMinutesFormatSeparator)
    },
    {
      key: 'last name',
      title: t('form.last_name'),
      render: (_, { person }) => person?.lastName || '-'
    },
    {
      key: 'first name',
      title: t('form.first_name'),
      render: (_, { person }) => person?.firstName || '-'
    },
    {
      key: 'dispensing date',
      title: t('stock_administration.release_date'),
      render: (_, { dispensingDate }) =>
        dispensingDate
          ? dayjs(dispensingDate).format(dayMonthYearHourMinutesFormatSeparator)
          : '-'
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) =>
        status ? <PrescriptionsStatusTag status={status} /> : '-'
    },
    {
      key: 'actions',
      render: (_, { id, status, treatmentPlanId }) =>
        status === RecipeStatus.NEW && (
          <Flex gap={8}>
            <Popconfirm
              title={t('recipes.decline_confirm_title')}
              description={t('common.delete_description')}
              onConfirm={() => onDecline(String(id), String(treatmentPlanId))}
            >
              <Button
                loading={isDispensing || isDeclining}
                size="small"
                danger
                type="primary"
              >
                {t('transfer.reject')}
              </Button>
            </Popconfirm>

            <Popconfirm
              title={t('recipes.dispense_confirm_title')}
              description={t('common.delete_description')}
              onConfirm={() => onDispense(String(id), String(treatmentPlanId))}
            >
              <Button
                loading={isDispensing || isDeclining}
                size="small"
                type="primary"
              >
                {t('roles.confirm')}
              </Button>
            </Popconfirm>
          </Flex>
        )
    },
    {
      key: 'details',
      fixed: 'right',
      render: (_, { id, treatmentPlanId }) => (
        <Button
          type="link"
          onClick={() =>
            navigate(
              paths.prescriptions.details(String(treatmentPlanId), String(id))
            )
          }
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  return (
    <PageBreadcrumbs
      items={[{ title: t('menu.prescriptions') }]}
      title={t('menu.prescriptions')}
    >
      <SearchBar placeholder={t('recipes.placeholder')} onSearch={onSearch} />

      <Divider className={styles.divider} />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        dataSource={data?.data}
        columns={columns}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
